import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  Calculator,
  Database,
  Scale,
  Coins,
  Users,
  TrendingDown,
  Clock,
  AlertTriangle,
  Sparkles,
  ClipboardCheck,
  ShieldCheck,
  PlayCircle,
  Building2,
  RefreshCw,
  Briefcase,
  CheckCircle2,
  Award,
  BarChart3,
  UserPlus,
  ArrowUpRight,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / campagnes / recrutement ————————————————
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Le constat (3 cartes chiffrées) ————————————————————————
const dilemmaStats = [
  {
    icon: Coins,
    metric: '80–120 k€',
    title: 'Coût annuel d’un DRH senior',
    description:
      "Charges incluses, pour un profil expérimenté capable de porter à la fois la transformation, la conformité et la stratégie sociale. Beaucoup de PME ne peuvent pas — ou ne veulent pas — engager une telle ligne sur leur masse salariale.",
  },
  {
    icon: Clock,
    metric: '6 mois',
    title: 'Délai moyen pour recruter un DRH',
    description:
      "Sourcing, entretiens, négociation, préavis, ramp-up. Pendant ce temps, vos chantiers RH critiques (paie, accord collectif, SIRH) attendent — ou dérapent.",
  },
  {
    icon: TrendingDown,
    metric: '× 5',
    title: 'Ratio coût mission vs embauche',
    description:
      "Pour un projet RH ponctuel (refonte SIRH, accord collectif, plan de rémunération), externaliser le pilotage à un DRH senior coûte généralement 5 fois moins qu’un recrutement dédié à temps plein.",
  },
];

// — Section 3 — Les 6 domaines (marque employeur en 01) ————————————————
const domains = [
  {
    icon: Heart,
    title: 'Marque employeur',
    description:
      "Plan d’action complet : EVP, contenus, diffusion, mesure d’impact. Et pour le déploiement opérationnel des parcours d’intégration côté collaborateur, nous nous appuyons sur des outils partenaires éprouvés.",
    partnerLink: {
      label: 'Loocatme',
      href: 'https://loocatme.fr',
      context: ', solution no-code dédiée à l’expérience candidat-collaborateur 360°.',
    },
  },
  {
    icon: Calculator,
    title: 'Paie externalisée',
    description:
      "Bulletins, déclarations sociales, gestion des absences, suivi des variables. Conformité réglementaire à jour, transmission fluide aux experts-comptables. Vous reprenez du temps sur ce qui crée vraiment de la valeur.",
  },
  {
    icon: Database,
    title: 'Refonte de votre SIRH',
    description:
      "Audit de l’existant, sélection des éditeurs, paramétrage, conduite du changement. Que vous passiez de l’Excel à un vrai outil ou que vous changiez de SIRH, nous pilotons le projet de bout en bout.",
    partnerPrefix: ' En partenariat avec ',
    partnerLink: {
      label: 'Thomaz RH',
      href: 'https://thomazrh-cabinetdeconseilsirh.com/',
      context: ', cabinet de conseil SIRH spécialisé sur les projets de transformation.',
    },
  },
  {
    icon: Scale,
    title: 'Préparation d’accord collectif',
    description:
      "Cadrage juridique, animation des négociations, rédaction. Accords NAO, télétravail, classification, intéressement. Nous sécurisons le process sans vous exposer à un faux pas social.",
  },
  {
    icon: Coins,
    title: 'Plan de rémunération collectif',
    description:
      "Grille salariale, classification, structure variable, intéressement, équité interne. Une politique salariale claire, qui retient vos talents et qui tient face aux comparaisons marché.",
  },
  {
    icon: RefreshCw,
    title: 'Réorganisation',
    description:
      "Restructurations, fusions d’équipes, mobilités internes, transformation managériale. Nous accompagnons les sujets sensibles avec la rigueur juridique et l’humanité que ces projets demandent.",
  },
];

// — Section 5 — Méthode 4 étapes ————————————————————————————————
const methodSteps = [
  {
    icon: ClipboardCheck,
    title: 'Cadrage gratuit (1 h)',
    description:
      "Nous identifions ensemble le besoin réel, les contraintes et les enjeux. Si recruteurs.io n’est pas la bonne solution, nous vous orientons.",
  },
  {
    icon: AlertTriangle,
    title: 'Devis chiffré au livrable',
    description:
      "Pas de jour-homme à rallonge. Un livrable, un prix, un délai. Vous savez ce que vous achetez avant de signer.",
  },
  {
    icon: Award,
    title: 'Mission pilotée par un DRH senior',
    description:
      "Pas de juniors envoyés sur le front. C’est un DRH expérimenté qui pilote, exécute et vous restitue.",
  },
  {
    icon: ShieldCheck,
    title: 'Livrable utilisable en interne',
    description:
      "Tout est documenté pour que votre équipe puisse reprendre la main, sans dépendance. Vous achetez de l’expertise, pas une accoutumance.",
  },
];

// — Section 6 — Pour qui ————————————————————————————————————————————
const fitProfiles = [
  {
    icon: Building2,
    title: 'PME en croissance, 30 à 150 collaborateurs',
    description:
      "Vous n’avez pas encore de DRH interne. Vos chantiers RH s’accumulent et s’improvisent. Vous voulez professionnaliser sans embaucher tout de suite.",
  },
  {
    icon: Briefcase,
    title: 'ETI dont le DRH est submergé',
    description:
      "Votre DRH a la tête dans le guidon. Il a besoin de bras experts sur des projets ponctuels (paie, SIRH, NAO) sans déléguer la stratégie.",
  },
  {
    icon: RefreshCw,
    title: 'Phase de transformation',
    description:
      "Réorganisation, fusion, restructuration. Vous avez besoin de compétences que vous ne garderez pas en interne une fois le projet terminé.",
  },
  {
    icon: Sparkles,
    title: 'Professionnaliser une fonction RH',
    description:
      "Votre RH est née dans l’urgence et ne tient plus la route. Vous voulez la mettre à niveau sans tout casser.",
  },
];

// — Section 7 — Différenciateurs ————————————————————————————————————
const differentiators = [
  {
    icon: Award,
    title: 'Vs les Big 4 (Deloitte, KPMG, EY, PwC)',
    description:
      "Pas de cadrage à 30 k€ avant le premier livrable. Pas de présentations 80 slides. Du concret, vite, à un tarif PME-ETI.",
  },
  {
    icon: Users,
    title: 'Vs un consultant indépendant',
    description:
      "Un DRH senior + une équipe technique + un outillage IA + la Plateforme Parcours Découverte. Pas de single point of failure si votre interlocuteur est en vacances.",
  },
  {
    icon: ShieldCheck,
    title: 'Vs un nouveau DRH interne',
    description:
      "Pas de ramp-up de 6 mois, pas de risque de désalignement, pas d’engagement long. Vous activez exactement quand vous en avez besoin, et vous arrêtez quand le projet est livré.",
  },
];

// — Section 4 — Cas client paie (Silae 180 bulletins) ————————————————
const paieCaseBullets = [
  'Mise en place complète du logiciel de paie Silae sur 180 bulletins mensuels',
  'Conception d’un protocole de préparation et de validation de paie en 4 contrôles successifs',
  'Atteinte de l’objectif « 0 erreur paie » en 3 mois',
  'Documentation transmise pour reprise en main par l’équipe interne',
];

export default function ExternalisationPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="DRH externalisé Marseille · Cadrage gratuit en 48h | recruteurs.io"
      description="DRH à la demande Marseille, Aix, PACA. Paie, SIRH, marque employeur, accord collectif, plan rémunération. Pilotage senior, devis au livrable. Cadrage gratuit en 48h."
      keywords="externalisation rh marseille, drh externalisé, drh à la demande, paie externalisée, refonte sirh, accord collectif pme, plan de rémunération, réorganisation entreprise, marque employeur paca"
    >
      <WebPageSchema
        page={{
          title: 'Externalisation RH : DRH à la demande à Marseille, Aix, PACA | recruteurs.io',
          description:
            "recruteurs.io prend en charge vos chantiers RH stratégiques à la demande : marque employeur, paie externalisée, refonte SIRH, accord collectif, plan de rémunération, réorganisation. Pilotage par un DRH senior, livrable par livrable, sans engagement long.",
          url: 'https://recruteurs.io/entreprise/externalisation',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Entreprise', url: 'https://recruteurs.io/entreprise/recrutement' },
          { name: 'Externalisation', url: 'https://recruteurs.io/entreprise/externalisation' },
        ]}
      />

      {/* HERO */}
      <HeroTemplateEnts
        page="externalisation"
        title="L’expertise RH stratégique, sans le coût d’un DRH à temps plein."
        eyebrow="Externalisation RH · DRH à la demande · Marseille · Aix · PACA"
        description="Marque employeur, paie, refonte SIRH, accord collectif, plan de rémunération, réorganisation : six chantiers RH critiques que recruteurs.io prend en charge à la demande, livrable par livrable. Vous gardez le contrôle, on apporte l’expertise et les bras."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Cabinet-de-recrutement-marseille.webp"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise/recrutement' },
          { name: 'Externalisation', href: '/entreprise/externalisation' },
        ]}
        cta1={{
          label: 'Cadrer mon besoin',
          onClick: () => navigate('/reservation'),
        }}
        cta2={{
          label: 'Voir nos domaines',
          href: '#domaines',
        }}
      />

      {/* SECTION 2 — Le dilemme */}
      <SectionBackground page="externalisation" section="ext-dilemma" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le dilemme des PME en croissance</span>

            <h2 className={h2Class}>
              Recruter un DRH coûte cher.{' '}
              <span className={gradientText}>Faire sans, encore plus.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Un DRH senior, c’est 80 à 120 k€ par an, sans compter les charges.
              Ne pas en avoir, c’est risquer un accord collectif mal cadré, une
              refonte SIRH qui dérape, ou une politique de rémunération qui fait
              fuir vos meilleurs éléments.{' '}
              <strong>recruteurs.io vous donne accès à cette expertise — uniquement quand vous en avez besoin, et uniquement pour ce dont vous avez besoin.</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dilemmaStats.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {item.metric}
                    </p>
                  </div>

                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — 6 domaines */}
      <SectionBackground
        page="externalisation"
        section="ext-domains"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div id="domaines" className="max-w-4xl mx-auto text-center mb-14 scroll-mt-24">
            <span className={eyebrowClass}>Nos domaines d’intervention</span>

            <h2 className={h2Class}>
              Six chantiers RH critiques.{' '}
              <span className={gradientText}>Un seul interlocuteur senior.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Chaque domaine est cadré comme un projet&nbsp;: un livrable précis,
              un délai, un prix. Pas de jour-homme à rallonge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((domain, index) => {
              const Icon = domain.icon;
              return (
                <motion.div
                  key={domain.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{domain.title}</h3>
                  <p className={bodyClass}>
                    {domain.description}
                    {domain.partnerLink && (
                      <>
                        {domain.partnerPrefix ?? ' '}
                        <a
                          href={domain.partnerLink.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                        >
                          {domain.partnerLink.label}
                        </a>
                        {domain.partnerLink.context}
                      </>
                    )}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Cas client paie (Silae 180 bulletins) */}
      <SectionBackground page="externalisation" section="ext-case-paie" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>Cas client · Paie externalisée</span>

            <h2 className={h2Class}>
              0 erreur paie en 3 mois{' '}
              <span className={gradientText}>sur 180 bulletins.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Un de nos clients gérait sa paie en interne avec un taux d’erreur
              récurrent qui pesait sur la confiance des salariés et la crédibilité
              de la fonction RH. Nous avons repris la paie de A à Z.
            </p>
          </div>

          <div className="rounded-[2rem] bg-[#fbfaf8] p-8 md:p-10 ring-1 ring-neutral-200 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                  Mission · Mise en place Silae + protocole 0 erreur
                </p>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight">
                  Un protocole de paie{' '}
                  <span className={gradientText}>rigoureux, mesurable, transmissible.</span>
                </h3>
                <p className={`${bodyClass} mt-4`}>
                  La méthode&nbsp;: 4 contrôles successifs sur chaque cycle de paie,
                  documentés, traçables. Aucune erreur n’atteint le bulletin du
                  collaborateur. Le savoir-faire reste chez vous une fois la
                  mission terminée.
                </p>
              </div>

              <ul className="space-y-4">
                {paieCaseBullets.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 rounded-2xl bg-white px-5 py-4 ring-1 ring-neutral-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base text-neutral-800 leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Méthode */}
      <SectionBackground page="externalisation" section="ext-method" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode</span>

            <h2 className={h2Class}>
              Cadrage gratuit. Devis chiffré.{' '}
              <span className={gradientText}>Livrable à valeur d’usage.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-5 h-5 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Pour qui */}
      <SectionBackground page="externalisation" section="ext-fit" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pour qui</span>

            <h2 className={h2Class}>
              L’externalisation RH recruteurs.io est faite pour vous{' '}
              <span className={gradientText}>si&hellip;</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fitProfiles.map((profile, index) => {
              const Icon = profile.icon;
              return (
                <motion.div
                  key={profile.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{profile.title}</h3>
                  <p className={bodyClass}>{profile.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Différenciateurs */}
      <SectionBackground page="externalisation" section="ext-diff" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre différenciateur</span>

            <h2 className={h2Class}>
              La seule agence à Marseille qui combine expertise RH stratégique,{' '}
              <span className={gradientText}>IA et Plateforme Parcours Découverte.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {differentiators.map((d, index) => {
              const Icon = d.icon;
              return (
                <motion.div
                  key={d.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{d.title}</h3>
                  <p className={bodyClass}>{d.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — Vidéo */}
      <SectionBackground page="externalisation" section="ext-press" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>

            <h2 className={h2Class}>
              Notre vision du recrutement et de la fonction RH,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé — vision du recrutement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 bis — Famille LoocatMe (cross-link contextuel : seul endroit où la cible recoupe) */}
      <SectionBackground
        page="externalisation"
        section="ext-family"
        className="bg-white"
      >
        <Section>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Solutions sœurs</span>
              <h2 className={h2Class}>
                Externaliser, c’est aussi{' '}
                <span className={gradientText}>
                  s’appuyer sur les bons outils.
                </span>
              </h2>
              <p className={sectionSubtitleClass}>
                Notre agence fait partie de la famille <strong>LoocatMe</strong>.
                Selon vos enjeux, deux solutions sœurs peuvent compléter notre
                accompagnement.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  Icon: BarChart3,
                  name: 'RESOO',
                  url: 'https://resoo.fr/',
                  badge: 'Pour réseaux retail multi-sites',
                  title: 'Le cockpit de votre masse salariale.',
                  description:
                    "Vous pilotez un réseau de magasins ou d’agences ? RESOO transforme votre data salariale en verdicts de recrutement par site, en temps réel. Pas de dashboard décoratif : des décisions actionnables.",
                },
                {
                  Icon: UserPlus,
                  name: 'LoocatMe',
                  url: 'https://loocatme.fr/',
                  badge: 'Pour automatiser l’onboarding',
                  title: 'L’onboarding clé en main, déployé en quelques jours.',
                  description:
                    "Plateforme No Code + contenus prêts à l’emploi. Pour les entreprises qui veulent industrialiser leur intégration sans construire un LMS de zéro — outils et contenus livrés ensemble.",
                },
              ].map((brand) => {
                const Icon = brand.Icon;
                return (
                  <a
                    key={brand.name}
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col rounded-3xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/40 hover:shadow-xl shadow-sm p-7 transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md shadow-[#d03484]/20">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-widest text-neutral-500 bg-[#fbfaf8] ring-1 ring-neutral-200 rounded-full px-3 py-1">
                        {brand.badge}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base font-bold text-[#30475e]">
                        {brand.name}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#d03484] transition-colors" />
                    </div>
                    <h3 className={h3Class}>{brand.title}</h3>
                    <p className={bodyClass}>{brand.description}</p>
                  </a>
                );
              })}
            </div>

            <p className="text-center text-sm text-neutral-500 mt-8">
              Vous n’êtes pas obligé d’utiliser ces solutions pour travailler
              avec recruteurs.io — elles sont mentionnées si elles répondent à
              un besoin précis que vous avez aujourd’hui.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — CTA final */}
      <SectionBackground page="externalisation" section="ext-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Cadrons votre projet RH.{' '}
              <span className={gradientText}>Une heure, sans engagement.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Nous identifions le besoin réel, les contraintes, le bon livrable.
              Si recruteurs.io n’est pas la meilleure solution, nous vous orientons.
            </p>

            <button
              onClick={() => navigate('/reservation')}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Réserver le cadrage (1 h)
            </button>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — Bloc SEO de fin */}
      <SectionBackground page="externalisation" section="ext-seo" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${h2Class} text-center`}>
              Externalisation RH à Marseille,{' '}
              <span className={gradientText}>Aix-en-Provence et PACA.</span>
            </h2>

            <div className="space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed mt-10">
              <p>
                recruteurs.io accompagne les PME et ETI de la région PACA sur
                l’ensemble des chantiers RH stratégiques&nbsp;: marque employeur,{' '}
                <strong>paie externalisée</strong>, refonte SIRH, accord
                collectif, plan de rémunération, réorganisation. Un DRH senior
                pilote chaque mission, livrable par livrable, sans engagement long.
              </p>
              <p>
                Sur les sujets de transformation digitale RH les plus complexes,
                nous travaillons aux côtés de{' '}
                <a
                  href="https://thomazrh-cabinetdeconseilsirh.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  spécialistes du conseil SIRH
                </a>
                , implantés à Poitiers, Bordeaux et Paris, pour offrir à nos
                clients une couverture nationale sans renoncer à l’exigence locale.
              </p>
              <p>
                Et pour aller plus loin sur l’intégration des nouvelles recrues,
                notre{' '}
                <a
                  href="/decouvrir"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  Plateforme Parcours Découverte
                </a>{' '}
                se complète naturellement avec{' '}
                <a
                  href="https://loocatme.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  Loocatme, plateforme d’onboarding personnalisé
                </a>
                . Deux briques complémentaires&nbsp;: nous concevons l’expérience
                candidat-collaborateur, eux la déploient en no-code.
              </p>
              <p>
                Vous cherchez plutôt à recruter sur un poste précis ? Voyez notre
                page{' '}
                <a
                  href="/entreprise/recrutement"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  recrutement à l’unité
                </a>
                . Vous gérez du volume ou de la récurrence ? Voyez nos{' '}
                <a
                  href="/entreprise/campagnes"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  campagnes de recrutement
                </a>
                . Notre modèle financier reste{' '}
                <a
                  href="/infos/tarifs"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  transparent et adaptable
                </a>{' '}
                à chaque format.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
